/**
 * The two ways this product scores discrimination — how well a question
 * separates strong examinees from weak ones.
 *
 * There are two because item analysis has two conventions and faculty do not
 * share one. **PBI** (point-biserial) correlates getting the item right with
 * total score, and is what most psychometric reports lead with. The
 * **discrimination index D** subtracts the bottom third's pass rate from the top
 * third's, which is the number a reviewer can recompute by hand and the one
 * older accreditation templates ask for. They measure the same property and
 * disagree at the margins, so a question that reads Adequate under one can read
 * Weak under the other — which is the point of offering both rather than picking
 * for the author.
 *
 * Bands live here; surfaces decide how to draw them. `lib/pbi-bands.ts` keeps
 * PBI's own bands and explainer, because the composer inspector reports that one
 * measure on its own.
 */

import {
  PBI_ADEQUATE_MIN,
  PBI_BANDS,
  PBI_EXPLAINER,
  PBI_STRONG_MIN,
  pbiBand,
  type PbiBand,
} from "@/lib/pbi-bands"
import {
  discriminationIndex,
  type AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"

export type DiscriminationMethodId = "pbi" | "index"

/** D's own cut points. Ebel's convention, rounded to the tiers PBI also uses. */
export const D_INDEX_STRONG_MIN = 0.4
export const D_INDEX_ADEQUATE_MIN = 0.2

const D_INDEX_BANDS: readonly PbiBand[] = PBI_BANDS.map((band) => {
  if (band.key === "strong") {
    return { ...band, rangeLabel: "Separates well (0.40+)" }
  }
  if (band.key === "adequate") {
    return { ...band, rangeLabel: "Separates somewhat (0.20 to 0.39)" }
  }
  if (band.key === "weak") {
    return { ...band, rangeLabel: "Doesn't separate (below 0.20)" }
  }
  return band
})

const BY_KEY = new Map(D_INDEX_BANDS.map((band) => [band.key, band]))

/** Exported so `question-signals` can band a D reading it already holds. */
export function dIndexBand(value: number | null): PbiBand {
  if (value === null) return BY_KEY.get("none")!
  if (value >= D_INDEX_STRONG_MIN) return BY_KEY.get("strong")!
  if (value >= D_INDEX_ADEQUATE_MIN) return BY_KEY.get("adequate")!
  return BY_KEY.get("weak")!
}

export interface DiscriminationMethod {
  id: DiscriminationMethodId
  /** Names the method in the switch. */
  label: string
  /** Names the measure in a heading or a row: "PBI 0.42". */
  abbreviation: string
  icon: string
  explainer: string
  bands: readonly PbiBand[]
  /** `null` when the item has never been sat, which is not a low score. */
  read: (row: AssessmentBankQuestion) => number | null
  band: (row: AssessmentBankQuestion) => PbiBand
}

export const DISCRIMINATION_METHODS: readonly DiscriminationMethod[] = [
  {
    id: "pbi",
    label: "Point-biserial",
    abbreviation: "PBI",
    // A scatter of right answers against total score is what the correlation is.
    icon: "fa-chart-scatter",
    explainer: PBI_EXPLAINER,
    bands: PBI_BANDS,
    read: (row) => {
      const value = Number(row.pbi)
      return Number.isNaN(value) ? null : value
    },
    band: (row) => pbiBand(row.pbi),
  },
  {
    id: "index",
    label: "Discrimination index",
    abbreviation: "D",
    // Two groups pulling apart: the top third's pass rate over the bottom's.
    icon: "fa-arrow-up-right-dots",
    explainer:
      "Discrimination index D. The share of the strongest third of a cohort who answered correctly, minus the share of the weakest third. 0.40 and above is strong; below 0.20 the item is not separating examinees.",
    bands: D_INDEX_BANDS,
    read: (row) => discriminationIndex(row),
    band: (row) => dIndexBand(discriminationIndex(row)),
  },
]

/** The heading glyph for the measure itself, not for either method. */
export const DISCRIMINATION_ICON = "fa-arrow-down-up-across-line"

export function discriminationMethod(
  id: DiscriminationMethodId
): DiscriminationMethod {
  return (
    DISCRIMINATION_METHODS.find((method) => method.id === id) ??
    DISCRIMINATION_METHODS[0]
  )
}

export { PBI_ADEQUATE_MIN, PBI_STRONG_MIN }
